
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Rocket from 'plaid-threads/Icons/Rocket';
import Libraries from 'plaid-threads/Icons/Libraries';
import Overview from '../../../components/docs/Overview';
import Actions from '../../../components/docs/Actions';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'View holdings and transactions from investment accounts'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Callout = makeShortcode("Callout");
const YouTubeEmbed = makeShortcode("YouTubeEmbed");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <Overview.Header product={'Investments'}>
  {metadata.description}
    </Overview.Header>
    <Actions.SmallWrapper miniTitle='Get started with Investments'>
  <Actions.SmallCard icon={<Libraries mdxType="Libraries" />} title='API Reference' link='/docs/api/products/investments/' />
  <Actions.SmallCard icon={<Rocket mdxType="Rocket" />} title='Quickstart' link='/docs/quickstart' />
    </Actions.SmallWrapper>
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`The Investments product allows you to obtain holding, security, and transactions data for investment-type accounts in financial institutions within the United States and Canada. This data can be used for personal financial management tools and wealth management analysis.`}</p>
    <Callout mdxType="Callout">
      <p>{`Looking for Plaid's solution to automate ACATS transfers and avoid friction, failures, and delays due to manual data entry? See `}<a parentName="p" {...{
          "href": "/docs/investments-move"
        }}>{`Investments Move`}</a>{` instead.`}</p>
    </Callout>
    <YouTubeEmbed videoId='DYiKaQgYJ74' alignment='full' title='Plaid Investments in 3 minutes' caption='Prefer to learn by watching? Get an overview of how Investments works in just 3 minutes!' mdxType="YouTubeEmbed" />
    <h4 {...{
      "id": "integration-process"
    }}>{`Integration process`}</h4>
    <p>{`Below is a high level overview of the Investments integration process. For a more detailed walkthrough, see `}<a parentName="p" {...{
        "href": "/docs/investments/add-to-app"
      }}>{`Add Investments to your app`}</a>{`.`}</p>
    <Callout title="Linking multiple institutions in one session" mdxType="Callout">
      <p>{`If your use case involves seeing a user's full financial picture across multiple institutions, you can use `}<a parentName="p" {...{
          "href": "/docs/link/multi-item-link"
        }}>{`Multi-Item Link`}</a>{` to let users link multiple accounts in a single Link session.`}</p>
    </Callout>
    <ol>
      <li parentName="ol">{`Create a Link token by calling `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{` with `}<inlineCode parentName="li">{`investments`}</inlineCode>{` in the `}<inlineCode parentName="li">{`products`}</inlineCode>{` array.`}
        <ul parentName="li">
          <li parentName="ul">{`If your use case involves a user linking Items at multiple institutions, consider using `}<a parentName="li" {...{
              "href": "/docs/link/multi-item-link"
            }}>{`Multi-Item Link`}</a>{`.`}</li>
        </ul>
      </li>
      <li parentName="ol">{`Initialize Link with the Link token from the previous step. For more details, see `}<a parentName="li" {...{
          "href": "/docs/link"
        }}>{`Link`}</a>{`.`}</li>
      <li parentName="ol">{`Once the user has successfully completed the Link flow, exchange the `}<inlineCode parentName="li">{`public_token`}</inlineCode>{` for an `}<inlineCode parentName="li">{`access_token`}</inlineCode>{` by calling `}<inlineCode parentName="li">{`/item/public_token/exchange`}</inlineCode>{`.`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/investments/holdings/get`}</inlineCode>{` to retrieve security and holding data, and/or `}<inlineCode parentName="li">{`/investments/transactions/get`}</inlineCode>{` to retrieve investment transactions data, for the Item.`}</li>
      <li parentName="ol">{`Listen for the `}<a parentName="li" {...{
          "href": "/docs/api/products/investments/#holdings-default_update"
        }}><inlineCode parentName="a">{`HOLDINGS: DEFAULT_UPDATE`}</inlineCode></a>{` and `}<a parentName="li" {...{
          "href": "/docs/api/products/investments/#investments_transactions-default_update"
        }}><inlineCode parentName="a">{`INVESTMENTS_TRANSACTIONS: DEFAULT_UPDATE`}</inlineCode></a>{` webhooks to know when updated data is available.`}</li>
    </ol>
    <h4 {...{
      "id": "securities-and-holdings"
    }}>{`Securities and holdings`}</h4>
    <p>{`The `}<inlineCode parentName="p">{`/investments/holdings/get`}</inlineCode>{` endpoint provides both security data and holding data. Security data represents information about a specific security, such as its name, ticker symbol, and price. Security data is not specific to a user's account; any user who held the same security at the same financial institution at the same time would have identical security data.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Sample security data",
        "#Sample": true,
        "security": true,
        "data": true
      }}>{`{
  "close_price": 10.42,
  "close_price_as_of": null,
  "cusip": "258620103",
  "institution_id": null,
  "institution_security_id": null,
  "is_cash_equivalent": false,
  "isin": "US2586201038",
  "iso_currency_code": "USD",
  "name": "DoubleLine Total Return Bond Fund",
  "proxy_security_id": null,
  "security_id": "NDVQrXQoqzt5v3bAe8qRt4A7mK7wvZCLEBBJk",
  "sedol": null,
  "ticker_symbol": "DBLTX",
  "type": "mutual fund",
  "subtype": "mutual fund",
  "unofficial_currency_code": null,
  "update_datetime": null,
  "sector": "Miscellaneous",
  "industry": "Investment Trusts or Mutual Funds",
  "market_identifier_code": "XNAS",
  "cfi_code": "CIOIBS",
  "figi": "BBG000JBSV40",
  "option_contract": null,
  "fixed_income": null
}
`}</code></pre>
    <p>{`Holding data, by contrast, represents information about a user's specific ownership of that security, such as the number of shares owned and the cost basis. Each holding includes a `}<inlineCode parentName="p">{`security_id`}</inlineCode>{` field that can be cross-referenced to a security for more detailed information about the security itself. Each holding also includes a `}<inlineCode parentName="p">{`tax_lots`}</inlineCode>{` array breaking out the cost basis, purchase price, and purchase date of each acquisition lot, or an empty array when the institution provides no lot-level data.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Sample holding data",
        "#Sample": true,
        "holding": true,
        "data": true
      }}>{`{
  "account_id": "k67E4xKvMlhmleEa4pg9hlwGGNnnEeixPolGm",
  "cost_basis": 10,
  "institution_price": 10.42,
  "institution_price_as_of": null,
  "institution_price_datetime": null,
  "institution_value": 20.84,
  "iso_currency_code": "USD",
  "quantity": 2,
  "vested_quantity": null,
  "vested_value": null,
  "security_id": "NDVQrXQoqzt5v3bAe8qRt4A7mK7wvZCLEBBJk",
  "unofficial_currency_code": null,
  "tax_lots": [
    {
      "institution_lot_id": "1",
      "original_purchase_datetime": "2021-01-15T00:00:00Z",
      "quantity": 1,
      "purchase_price": 4.75,
      "cost_basis": 4.75,
      "current_value": 10.42,
      "position_type": "LONG"
    },
    {
      "institution_lot_id": "2",
      "original_purchase_datetime": "2021-03-22T00:00:00Z",
      "quantity": 1,
      "purchase_price": 5.25,
      "cost_basis": 5.25,
      "current_value": 10.42,
      "position_type": "LONG"
    }
  ]
}
`}</code></pre>
    <h4 {...{
      "id": "transactions"
    }}>{`Transactions`}</h4>
    <p>{`The `}<inlineCode parentName="p">{`/investments/transactions/get`}</inlineCode>{` endpoint provides up to 24 months of investment transactions data. The schema for investment transactions is not the same as for transactions data returned by the `}<a parentName="p" {...{
        "href": "/docs/transactions"
      }}>{`Transactions`}</a>{` product, instead providing securities-specific information. Inflow, such as stock sales, is shown as a negative amount, and outflow, such as stock purchases, is positive. The `}<inlineCode parentName="p">{`/investments/transactions/get`}</inlineCode>{` endpoint can only be used for investment-type accounts; for obtaining transaction history for other account types, use `}<a parentName="p" {...{
        "href": "/docs/transactions"
      }}>{`Transactions`}</a>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Sample transaction data",
        "#Sample": true,
        "transaction": true,
        "data": true
      }}>{`{
  "account_id": "rz99ex9ZQotvnjXdgQLEsR81e3ArPgulVWjGj",
  "amount": -8.72,
  "cancel_transaction_id": null,
  "date": "2020-05-29",
  "fees": 0,
  "investment_transaction_id": "oq99Pz97joHQem4BNjXECev1E4B6L6sRzwANW",
  "iso_currency_code": "USD",
  "name": "INCOME DIV DIVIDEND RECEIVED",
  "price": 0,
  "quantity": 0,
  "security_id": "eW4jmnjd6AtjxXVrjmj6SX1dNEdZp3Cy8RnRQ",
  "subtype": "dividend",
  "type": "cash",
  "unofficial_currency_code": null
}
`}</code></pre>
    <h4 {...{
      "id": "investments-transactions-initialization-behavior"
    }}>{`Investments transactions initialization behavior`}</h4>
    <p>{`Unlike the Transactions product, by default, Investments Transactions operates synchronously and will not fire a webhook to indicate when initial data is ready for an Item. If investments transactions data is not ready when `}<inlineCode parentName="p">{`/investments/transactions/get`}</inlineCode>{` is first called, Plaid will wait for the data. For this reason, calling `}<inlineCode parentName="p">{`/investments/transactions/get`}</inlineCode>{` immediately after Link may take up to one to two minutes to return.`}</p>
    <p>{`If you are adding Investments to an Item by calling `}<inlineCode parentName="p">{`/investments/transactions/get`}</inlineCode>{` `}<a parentName="p" {...{
        "href": "/docs/link/initializing-products/#adding-products-post-link"
      }}>{`after the Item was originally linked`}</a>{`, instead of specifying the Investments product in `}<inlineCode parentName="p">{`products`}</inlineCode>{`, `}<inlineCode parentName="p">{`required_if_supported_products`}</inlineCode>{`, or `}<inlineCode parentName="p">{`optional_products`}</inlineCode>{` when calling `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`, you can optionally request asynchronous behavior by specifying `}<inlineCode parentName="p">{`async_update=true`}</inlineCode>{`. In this case, Investments will fire a `}<a parentName="p" {...{
        "href": "/docs/api/products/investments/#investments_transactions-historical_update"
      }}><inlineCode parentName="a">{`HISTORICAL_UPDATE`}</inlineCode></a>{` webhook when data is ready to be fetched. In all other scenarios, Investments endpoints will operate synchronously and will not fire a webhook to indicate when the Item's initial data is available to be fetched.`}</p>
    <h4 {...{
      "id": "investments-updates-and-webhooks"
    }}>{`Investments updates and webhooks`}</h4>
    <p>{`Investments data is not static, since users' holdings will change as they trade and as market prices fluctuate. Plaid checks for updates to investment data at least once per day during market days (up to 2-4 times per day, depending on institution); most holding changes are detected overnight, after market hours, once prices settle.
You can also request an update on-demand via the `}<inlineCode parentName="p">{`/investments/refresh`}</inlineCode>{` endpoint, which is available as an add-on for Investments customers. To request access to this endpoint, submit a `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/team/products"
      }}>{`product access request`}</a>{` or contact your Plaid account manager.`}</p>
    <p>{`There are two webhooks that are used for informing you of changes to investment data. The `}<a parentName="p" {...{
        "href": "/docs/api/products/investments/#holdings-default_update"
      }}><inlineCode parentName="a">{`DEFAULT_UPDATE`}</inlineCode></a>{` webhook of type `}<inlineCode parentName="p">{`HOLDINGS`}</inlineCode>{` fires when new holdings have been detected or the quantity or price of an existing holding has changed. The `}<a parentName="p" {...{
        "href": "/docs/api/products/investments/#investments_transactions-default_update"
      }}><inlineCode parentName="a">{`DEFAULT_UPDATE`}</inlineCode></a>{` webhook of type `}<inlineCode parentName="p">{`INVESTMENTS_TRANSACTIONS`}</inlineCode>{` fires when a new or canceled investment transaction has been detected.`}</p>
    <p>{`When updating an Item with new Investments transactions data, it is recommended to call `}<inlineCode parentName="p">{`/investments/transactions/get`}</inlineCode>{` with only the date range that needs to be updated, rather than the maximum available date range, in order to reduce the amount of data that you must receive and process.`}</p>
    <h4 {...{
      "id": "investments-institution-coverage"
    }}>{`Investments institution coverage`}</h4>
    <p>{`By default, Investments provides access to data at over 3,200 institutions in the US and Canada. For details, see `}<a parentName="p" {...{
        "href": "/docs/institutions"
      }}>{`Institution Coverage`}</a>{`.`}</p>
    <p>{`Access to Fidelity Investments is granted automatically for all new customers on Growth or Custom plans and becomes effective approximately 4 weeks after Production access has been granted. If you need faster access for business reasons, contact your account manager.`}</p>
    <p>{`Customers on Pay-as-you-go plans must manually request access to Fidelity by going to the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/activity/status/oauth-institutions"
      }}>{`OAuth institutions page`}</a>{` and clicking "Request access".`}</p>
    <p>{`Access to Charles Schwab is granted automatically for all customers, but may take up to 5 business days.`}</p>
    <h4 {...{
      "id": "testing-investments"
    }}>{`Testing Investments`}</h4>
    <p>{`Investments can be tested in `}<a parentName="p" {...{
        "href": "/docs/sandbox"
      }}>{`Sandbox`}</a>{` without any additional permissions.`}</p>
    <p>{`To test with realistic data, the easiest way is to create a custom user with `}<a parentName="p" {...{
        "href": "/docs/sandbox/studio/"
      }}>{`Sandbox Studio`}</a>{`. `}</p>
    <p>{`Sandbox Studio's `}<strong parentName="p">{`John Smith Cash`}</strong>{` template is a ready-made Investments custom user with brokerage holdings. If provided real-world ticker symbols, Plaid will automatically populate securities with realistic data for both options and fixed income.`}</p>
    <p>{`When using the custom Sandbox user, Investments must be placed in the `}<inlineCode parentName="p">{`products`}</inlineCode>{` array of `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` and cannot be used in the `}<inlineCode parentName="p">{`optional_products`}</inlineCode>{`, `}<inlineCode parentName="p">{`additional_consented_products`}</inlineCode>{`, or `}<inlineCode parentName="p">{`required_if_supported_products`}</inlineCode>{` array. Omitting `}<inlineCode parentName="p">{`investments`}</inlineCode>{` from the `}<inlineCode parentName="p">{`products`}</inlineCode>{` array may cause custom Sandbox investments data not to be loaded.`}</p>
    <h4 {...{
      "id": "investments-pricing"
    }}>{`Investments pricing`}</h4>
    <p>{`Investments is billed on a `}<a parentName="p" {...{
        "href": "/docs/account/billing/#subscription-fee"
      }}>{`subscription model`}</a>{`; Investments Refresh is billed on a `}<a parentName="p" {...{
        "href": "/docs/account/billing/#per-request-flat-fee"
      }}>{`per-request flat fee model`}</a>{`. To view the exact pricing you may be eligible for, `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/overview/production"
      }}>{`apply for Production access`}</a>{` or `}<a parentName="p" {...{
        "href": "https://plaid.com/contact/"
      }}>{`contact sales`}</a>{`. For more details about pricing and billing models, see `}<a parentName="p" {...{
        "href": "/docs/account/billing"
      }}>{`Plaid billing`}</a>{`.`}</p>
    <h4 {...{
      "id": "next-steps"
    }}>{`Next steps`}</h4>
    <p>{`To get started building with Investments, see `}<a parentName="p" {...{
        "href": "/docs/investments/add-to-app"
      }}>{`Add Investments to your App`}</a>{`.`}</p>
    <p>{`If you're ready to launch to Production, see the Launch Center.`}</p>
    <Actions.Wrapper>
  <Actions.Card icon={<Rocket mdxType="Rocket" />} title='Launch Center' subtitle="See next steps to launch in Production" link='https://dashboard.plaid.com/developers/launch-center' linkText='Launch' />
    </Actions.Wrapper>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "integration-process",
        level: 4,
        title: "Integration process",
      },
{
        id: "securities-and-holdings",
        level: 4,
        title: "Securities and holdings",
      },
{
        id: "transactions",
        level: 4,
        title: "Transactions",
      },
{
        id: "investments-transactions-initialization-behavior",
        level: 4,
        title: "Investments transactions initialization behavior",
      },
{
        id: "investments-updates-and-webhooks",
        level: 4,
        title: "Investments updates and webhooks",
      },
{
        id: "investments-institution-coverage",
        level: 4,
        title: "Investments institution coverage",
      },
{
        id: "testing-investments",
        level: 4,
        title: "Testing Investments",
      },
{
        id: "investments-pricing",
        level: 4,
        title: "Investments pricing",
      },
{
        id: "next-steps",
        level: 4,
        title: "Next steps",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
